import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiStar, FiFolderPlus, FiFileText, FiZap, FiInfo, FiEdit } from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import GenerateAssistantForm from './GenerateAssistantForm';
import FormTemplate, { AssistantTemplate } from './FormTemplate';
import AssistantForm from './AssistantForm';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { addAssistant, updateAssistant } from '../store/slices/assistantSlice';
import { api } from '../utils/api';
import { createAssistant as createAssistantThunk, cloneAssistant as cloneAssistantThunk, generateAssistant as generateAssistantThunk, fetchAssistants } from '../store/slices/assistantSlice';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface CreateAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type CreationMode = 'selection' | 'template' | 'generate' | 'import' | 'blank' | 'flowbuilder' | 'custom';

const CreateAssistantModal: React.FC<CreateAssistantModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [mode, setMode] = useState<CreationMode>('selection');
  const [assistantData, setAssistantData] = useState<any>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<AssistantTemplate | null>(null);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [importId, setImportId] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const assistants = useAppSelector((s) => s.assistants.assistants) as any[];

  const isNameDuplicate = useMemo(() => {
    const target = (name || '').trim().toLowerCase();
    if (!target) return false;
    return (assistants || []).some((a) => (a?.name || '').trim().toLowerCase() === target);
  }, [assistants, name]);

  const handleModeSelect = (selectedMode: CreationMode) => {
    setMode(selectedMode);
  };

  const handleBack = () => {
    setMode('selection');
    setAssistantData(null);
    setSelectedTemplate(null);
  };

  const handleTemplateSelect = (template: AssistantTemplate) => {
    setSelectedTemplate(template);
    // Pre-populate form data from template
    setName(template.name);
    setDescription(template.description);
    setAssistantData(template.config);
  };

  const handleTemplateConfirm = () => {
    if (selectedTemplate) {
      setMode('custom');
    }
  };

  const handleCustomFormSuccess = (assistant: any) => {
    dispatch(addAssistant(assistant));
    toast.success('Assistant created from template');
    onSuccess();
    onClose();
  };

  const handleGenerateSuccess = (data: any) => {
    try {
      if (data?.assistantId) {
        // Refresh list and redirect to builder for the generated assistant
        dispatch(fetchAssistants() as any);
        onSuccess();
        onClose();
        navigate(`/assistants/${data.assistantId}/edit`);
        return;
      }
    } catch {}
    setAssistantData(data);
    setMode('flowbuilder');
  };

  // Unified create handler for all flows
  const handleCreateAssistant = async () => {
    setFormError('');
    if (!name.trim()) {
      setFormError('System Name is required');
      toast.error('Please enter a System Name');
      return;
    }
    if (isNameDuplicate) {
      setFormError('An assistant with this name already exists');
      toast.error('Duplicate name. Choose a unique System Name.');
      return;
    }
    try {
      setSubmitting(true);
      if (mode === 'blank') {
        const created = await dispatch(createAssistantThunk({
          name: name.trim(),
          description: description || 'Blank canvas assistant',
          systemPrompt: `You are ${name.trim()}, an AI assistant. Be helpful, concise, and professional.`,
          model: 'gpt-4-turbo-preview',
          temperature: 0.7,
          maxTokens: 1000,
          voiceEnabled: false,
        }) as any).unwrap();
        console.log('Assistant created:', created);
        // Don't call fetchAssistants() - createAssistant.fulfilled already added it to Redux
        toast.success('Assistant created');
        onSuccess();
        onClose();
        return;
      }
      if (mode === 'import') {
        if (!importId.trim()) {
          setFormError('Please enter an assistant ID to import');
          setSubmitting(false);
          return;
        }
        const cloned = await dispatch(cloneAssistantThunk(importId.trim()) as any).unwrap();
        // Don't call fetchAssistants() - cloneAssistant.fulfilled already added it to Redux
        toast.success('Assistant imported');
        onSuccess();
        onClose();
        return;
      }
      if (mode === 'flowbuilder') {
        const created = await dispatch(createAssistantThunk({
          name: name.trim(),
          description: description || 'Flow Builder assistant',
          systemPrompt: `You are ${name.trim()}, an AI assistant designed for flow-based conversations.`,
        model: 'gpt-4-turbo-preview',
        temperature: 0.7,
        maxTokens: 1000,
          voiceEnabled: false,
        }) as any).unwrap();
        // Don't call fetchAssistants() - createAssistant.fulfilled already added it to Redux
        onSuccess();
        onClose();
        navigate(`/assistants/${created.id}/edit`);
        return;
      }
      if (mode === 'generate') {
        // Quick AI PowerBuilder path. For advanced options, use the dedicated Generate form.
        const generated = await dispatch(generateAssistantThunk({
          assistantName: name.trim(),
          description: description,
        }) as any).unwrap();
        // Don't call fetchAssistants() - generateAssistant.fulfilled already added it to Redux
        toast.success('AI builder started');
        // If job returned, begin polling job status until completion
        try {
          if (generated && typeof generated.id === 'string' && generated.id.startsWith('tmp_job_')) {
            const jobId = generated.id.replace('tmp_', '');
            let tries = 0;
            const maxTries = 40; // ~2 minutes if interval is 3s
            const poll = async () => {
              tries += 1;
              try {
                const rsp = await api.get(`/assistants/generate/status/${jobId}`);
                if (rsp.data?.status === 'completed' && rsp.data?.assistantId) {
                  toast.success('Assistant generation completed');
                  dispatch(fetchAssistants() as any);
                  onSuccess();
                  onClose();
                  navigate(`/assistants/${rsp.data.assistantId}/edit`);
                  return;
                }
                if (rsp.data?.status === 'failed') {
                  toast.error('Assistant generation failed');
                  return;
                }
              } catch {}
              if (tries < maxTries) setTimeout(poll, 3000);
            };
            setTimeout(poll, 3000);
          } else {
            onSuccess();
            onClose();
          }
        } catch {
          onSuccess();
          onClose();
        }
        return;
      }
    } catch (err: any) {
      const backendError = err?.response?.data?.error || err?.message || 'Assistant creation failed';
      setFormError(backendError);
      toast.error(backendError);
    } finally {
      setSubmitting(false);
    }
  };

  const creationOptions = [
    {
      id: 'template',
      title: 'Use Template',
      description: 'Start with pre-configured templates for common use cases like customer service, sales, and support.',
      icon: FiFileText,
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-100',
      iconBg: 'bg-blue-500',
      delay: 0.05
    },
    {
      id: 'generate',
      title: 'Generate Assistant',
      description: 'Generate an assistant based off of your business\'s profile and a brief description.',
      icon: FiStar,
      color: 'from-purple-500 to-blue-500',
      bgColor: 'bg-purple-100',
      iconBg: 'bg-purple-500',
      delay: 0.1
    },
    {
      id: 'blank',
      title: 'Custom Assistant',
      description: 'Build a fully customized assistant with advanced settings, tools, and integrations.',
      icon: FiEdit,
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-100',
      iconBg: 'bg-orange-500',
      delay: 0.15
    },
    {
      id: 'import',
      title: 'Import With ID',
      description: 'Import an assistant using the unique ID to create a duplicate in your account.',
      icon: FiFolderPlus,
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-100',
      iconBg: 'bg-cyan-500',
      delay: 0.2
    }
  ];

  const conversationalOption = {
    id: 'flowbuilder',
    title: 'Flowbuilder',
    description: 'An objective-based conversational pathway builder. Helps with deterministic routes and AI focus.',
    icon: FiZap,
    color: 'from-pink-500 to-purple-500',
    bgColor: 'bg-pink-100',
    iconBg: 'bg-gradient-to-r from-pink-500 to-purple-500',
    delay: 0.4
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="bg-slate-900 rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
          style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="text-3xl font-bold text-white mb-2">Create Assistant</h2>
              <p className="text-gray-400">How would you like to create your next employee?</p>
            </motion.div>
            
            <motion.button
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2"
            >
              <FiX size={24} />
            </motion.button>
          </div>

          <AnimatePresence mode="wait">
            {mode === 'selection' && (
              <motion.div
                key="selection"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Assistant Builder Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-8"
                >
                  <h3 className="text-xl font-semibold text-white mb-6">Assistant Builder</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {creationOptions.map((option) => (
                      <motion.button
                        key={option.id}
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: option.delay, duration: 0.4 }}
                        whileHover={{ scale: 1.02, y: -5 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                            handleModeSelect(option.id as CreationMode);
                        }}
                        className="relative bg-slate-800 rounded-xl p-6 text-left border border-slate-700 hover:border-slate-600 transition-all duration-300 group overflow-hidden"
                      >
                        {/* Background Gradient on Hover */}
                        <motion.div
                          className={`absolute inset-0 bg-gradient-to-br ${option.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
                        />
                        
                        {/* Info Icon */}
                        <div className="absolute top-4 right-4">
                          <motion.div
                            whileHover={{ scale: 1.2 }}
                            className="text-slate-400 hover:text-slate-300"
                          >
                            <FiInfo size={16} />
                          </motion.div>
                        </div>

                        {/* Icon */}
                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ duration: 0.2 }}
                          className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${option.iconBg} text-white mb-4`}
                        >
                          <option.icon size={24} />
                        </motion.div>

                        {/* Content */}
                        <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-300 transition-colors">
                          {option.title}
                        </h4>
                        <p className="text-sm text-gray-400 leading-relaxed">
                          {option.description}
                        </p>

                        {/* Animated Border */}
                        <motion.div
                          className="absolute inset-0 rounded-xl border-2 border-transparent"
                          whileHover={{
                            borderImage: `linear-gradient(135deg, ${option.color.split(' ')[1]}, ${option.color.split(' ')[3]}) 1`,
                          }}
                        />
                      </motion.button>
                    ))}
                  </div>
                </motion.div>

                {/* Conversational Pathway Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <h3 className="text-xl font-semibold text-white">Conversational Pathway</h3>
                    <motion.span
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6 }}
                      className="px-3 py-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-semibold rounded-full"
                    >
                      BETA
                    </motion.span>
                  </div>

                  <motion.button
                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: 0.7, duration: 0.4 }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleModeSelect('flowbuilder')}
                    className="relative w-full bg-slate-800 rounded-xl p-6 text-left border border-slate-700 hover:border-slate-600 transition-all duration-300 group overflow-hidden"
                  >
                    {/* Background Gradient on Hover */}
                    <motion.div
                      className={`absolute inset-0 bg-gradient-to-br ${conversationalOption.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
                    />

                    {/* Info Icon */}
                    <div className="absolute top-4 right-4">
                      <motion.div
                        whileHover={{ scale: 1.2 }}
                        className="text-slate-400 hover:text-slate-300"
                      >
                        <FiInfo size={16} />
                      </motion.div>
                    </div>

                    {/* Icon */}
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.2 }}
                      className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${conversationalOption.iconBg} text-white mb-4`}
                    >
                      <conversationalOption.icon size={24} />
                    </motion.div>

                    {/* Content */}
                    <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-pink-300 transition-colors">
                      {conversationalOption.title}
                    </h4>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {conversationalOption.description}
                    </p>
                  </motion.button>
                </motion.div>

                {/* Footer */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="flex justify-end mt-8"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onClose}
                    className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Close
                  </motion.button>
                </motion.div>

                {/* Basic Details Form */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">System Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg bg-slate-800 border ${isNameDuplicate ? 'border-red-500' : 'border-slate-700'} text-white focus:outline-none focus:border-blue-500`}
                      placeholder="e.g., Sales Pro Assistant"
                    />
                    {isNameDuplicate && (
                      <div className="mt-1 text-xs text-red-400">Name already exists</div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Description (optional)</label>
                    <input
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-blue-500"
                      placeholder="Short description"
                    />
                  </div>
                  {/* Import field is only rendered within Import mode panel below to avoid TS narrowing issue here */}
                  {formError && (
                    <div className="md:col-span-2 text-sm text-red-400">{formError}</div>
                  )}
                </motion.div>
              </motion.div>
            )}

            {mode === 'generate' && (
              <GenerateAssistantForm
                onBack={handleBack}
                onSuccess={handleGenerateSuccess}
                onClose={onClose}
              />
            )}

            {mode === 'flowbuilder' && (
              <motion.div
                key="flowbuilder"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="text-center py-12"
              >
                <h3 className="text-xl font-semibold text-white mb-4">FlowBuilder</h3>
                <div className="max-w-md mx-auto text-left">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">System Name</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-blue-500"
                        placeholder="e.g., Flow Assistant"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Description (optional)</label>
                      <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-blue-500"
                        placeholder="Assistant for flow-based conversations"
                      />
                    </div>
                    {formError && <div className="text-sm text-red-400">{formError}</div>}
                  </div>
                  <div className="mt-6 flex items-center justify-center gap-3">
                    <button
                      onClick={handleBack}
                      className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                    >
                      Back
                    </button>
                    <button
                      disabled={submitting}
                      onClick={handleCreateAssistant}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/60 text-white rounded-lg transition-colors"
                    >
                      {submitting ? 'Creating...' : 'Create & Open Builder'}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {mode === 'import' && (
              <motion.div
                key="import"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="text-center py-12"
              >
                <h3 className="text-xl font-semibold text-white mb-4">Import Assistant</h3>
                <div className="max-w-md mx-auto">
                  <label className="block text-sm text-gray-400 mb-2">Existing Assistant ID</label>
                  <input
                    type="text"
                    value={importId}
                    onChange={(e) => setImportId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-blue-500"
                    placeholder="Enter assistant ID to clone"
                  />
                  {formError && (
                    <div className="mt-2 text-sm text-red-400">{formError}</div>
                  )}
                  <div className="mt-6 flex items-center justify-center gap-3">
                <button
                  onClick={handleBack}
                      className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back
                </button>
                    <button
                      disabled={submitting}
                      onClick={handleCreateAssistant}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/60 text-white rounded-lg transition-colors"
                    >
                      {submitting ? 'Importing...' : 'Import'}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {mode === 'template' && (
              <motion.div
                key="template"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="py-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Choose a Template</h3>
                    <p className="text-gray-400">
                      Select a pre-configured template to get started quickly
                    </p>
                  </div>
                  {selectedTemplate && (
                    <div className="flex gap-3">
                      <button
                        onClick={handleBack}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleTemplateConfirm}
                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        Continue with Template
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <FormTemplate
                    onSelectTemplate={handleTemplateSelect}
                    selectedTemplateId={selectedTemplate?.id}
                  />
                </div>
                
                {!selectedTemplate && (
                  <div className="mt-6 flex justify-center">
                    <button
                      onClick={handleBack}
                      className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                    >
                      Back to Selection
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {mode === 'custom' && selectedTemplate && (
              <motion.div
                key="custom"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-50"
              >
                <AssistantForm
                  initialData={selectedTemplate.config}
                  mode="create"
                  onClose={() => {
                    setMode('template');
                    setSelectedTemplate(null);
                  }}
                  onCreated={handleCustomFormSuccess}
                />
              </motion.div>
            )}

            {mode === 'blank' && (
              <motion.div
                key="blank"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-50"
              >
                <AssistantForm
                  mode="create"
                  onClose={handleBack}
                  onCreated={(assistant) => {
                    dispatch(addAssistant(assistant));
                    toast.success('Custom assistant created');
                    onSuccess();
                    onClose();
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
          {/* No global footer; each mode provides its own actions */}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreateAssistantModal;